import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import Layout from '@/components/Layout';
import Home from '@/pages/Home';
import CataloguePage from '@/pages/CataloguePage';
import AboutPage from '@/pages/AboutPage';
import NotFoundPage from '@/pages/NotFoundPage';
import { PrivacyPage, TermsPage, DeliveryPage, RefundPage } from '@/pages/LegalPages';

import AdminLogin from '@/pages/admin/AdminLogin';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminProducts from '@/pages/admin/AdminProducts';
import AdminProductsImport from '@/pages/admin/AdminProductsImport';
import AdminProductForm from '@/pages/admin/AdminProductForm';
import AdminPlans from '@/pages/admin/AdminPlans';
import AdminPlanForm from '@/pages/admin/AdminPlanForm';
import AdminBlog from '@/pages/admin/AdminBlog';
import AdminBlogForm from '@/pages/admin/AdminBlogForm';
import AdminOffers from '@/pages/admin/AdminOffers';
import AdminOfferForm from '@/pages/admin/AdminOfferForm';
import AdminProductRequests from '@/pages/admin/AdminProductRequests';
import AdminRequestsInbox from '@/pages/admin/AdminRequestsInbox';
import AdminQuoteRequests from '@/pages/admin/AdminQuoteRequests';
import AdminOperationsPage from '@/pages/admin/AdminOperationsPage';
import AdminPartnerBrandsPage from '@/pages/admin/AdminPartnerBrandsPage';
import AdminFounderCampaign from '@/pages/admin/AdminFounderCampaign';
import AdminSurveys from '@/pages/admin/AdminSurveys';
import SurveyPage from '@/pages/SurveyPage';
import { AdminLeads, AdminContacts, AdminCabinetCustomers } from '@/pages/admin/AdminViewers';
import AdminPendingCustomers from '@/pages/admin/AdminPendingCustomers';
import AdminDeliveriesCalendar from '@/pages/admin/AdminDeliveriesCalendar';
import AdminPurchases from '@/pages/admin/AdminPurchases';
import AdminDuePayments from '@/pages/admin/AdminDuePayments';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminBulkImport from '@/pages/admin/AdminBulkImport';
import AdminFollowUp from '@/pages/admin/AdminFollowUp';
import AdminHomepageContent from '@/pages/admin/AdminHomepageContent';
import AdminTestimonials from '@/pages/admin/AdminTestimonials';
import SetPasswordPage from '@/pages/SetPasswordPage';
import ConfirmDeliveryPage from '@/pages/ConfirmDeliveryPage';
import RequestProductPage from '@/pages/RequestProductPage';
import QuoteViewPage from '@/pages/QuoteViewPage';

// Customer Cabinet (merged from standalone MVP) — mounts under /cabinet/*
import CabinetApp from '@/cabinet/CabinetApp';

function CabinetMount() {
  return (
    <div className="cabinet-scope" data-testid="cabinet-scope">
      <CabinetApp />
    </div>
  );
}

// Legacy marketing routes (Plans, FAQ, Blog, Contact, How It Works, Special Offers)
// now redirect to the simplified single-page home. About Us has its own page
// again per the latest brief.
const REDIRECTS = [
  { from: '/plans', to: '/' },
  { from: '/faq', to: '/' },
  { from: '/blog', to: '/' },
  { from: '/blog/:slug', to: '/' },
  { from: '/contact', to: '/#contact' },
  { from: '/how-it-works', to: '/#how' },
  { from: '/special-offers', to: '/#partners' },
  { from: '/special-offers/toys-accessories', to: '/#partners' },
  { from: '/special-offers/innovation-tech', to: '/#partners' },
  { from: '/special-offers/services', to: '/#partners' },
];

function App() {
  return (
    <Routes>
      {/* Customer Cabinet (logged-in customer experience) */}
      <Route path="/cabinet/*" element={<CabinetMount />} />

      {/* Public bulk-import magic-link landing (no Layout to keep flow focused) */}
      <Route path="/set-password" element={<SetPasswordPage />} />
      {/* Public delivery-confirmation magic-link landing */}
      <Route path="/confirm-delivery" element={<ConfirmDeliveryPage />} />

      {/* Public marketing site */}
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutPage />} />

        <Route path="/catalogue" element={<CataloguePage />} />
        <Route path="/catalogue/request" element={<RequestProductPage />} />
        <Route path="/quote/:token" element={<QuoteViewPage />} />
        {/* Sub-category clicks from Home render the Catalogue filtered to that group */}
        <Route path="/catalogue/:category" element={<CataloguePage />} />

        <Route path="/survey" element={<SurveyPage />} />

        {/* Legacy marketing pages → redirect to anchors on the simplified home */}
        {REDIRECTS.map((r) => (
          <Route key={r.from} path={r.from} element={<Navigate to={r.to} replace />} />
        ))}

        {/* Legal pages (kept for compliance; no longer in main nav) */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/delivery-policy" element={<DeliveryPage />} />
        <Route path="/refund-policy" element={<RefundPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Admin panel */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="products/import" element={<AdminProductsImport />} />
        <Route path="products/new" element={<AdminProductForm />} />
        <Route path="products/:id" element={<AdminProductForm />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="plans/new" element={<AdminPlanForm />} />
        <Route path="plans/:id" element={<AdminPlanForm />} />
        <Route path="blog-posts" element={<AdminBlog />} />
        <Route path="blog-posts/new" element={<AdminBlogForm />} />
        <Route path="blog-posts/:id" element={<AdminBlogForm />} />
        <Route path="special-offers" element={<AdminOffers />} />
        <Route path="special-offers/new" element={<AdminOfferForm />} />
        <Route path="special-offers/:id" element={<AdminOfferForm />} />
        <Route path="cabinet-customers" element={<AdminCabinetCustomers />} />
        <Route path="pending-customers" element={<AdminPendingCustomers />} />
        <Route path="deliveries-calendar" element={<AdminDeliveriesCalendar />} />
        <Route path="followup" element={<AdminFollowUp />} />
        <Route path="purchases" element={<AdminPurchases />} />
        <Route path="due-payments" element={<AdminDuePayments />} />
        <Route path="bulk-import" element={<AdminBulkImport />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="product-requests" element={<AdminProductRequests />} />
        <Route path="requests/inbox" element={<AdminRequestsInbox />} />
        <Route path="quote-requests" element={<AdminQuoteRequests />} />
        <Route path="operations" element={<AdminOperationsPage />} />
        <Route path="partner-brands" element={<AdminPartnerBrandsPage />} />
        <Route path="founder-campaign" element={<AdminFounderCampaign />} />
        <Route path="surveys" element={<AdminSurveys />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="contact-inquiries" element={<AdminContacts />} />
        <Route path="homepage-content" element={<AdminHomepageContent />} />
        <Route path="testimonials" element={<AdminTestimonials />} />
      </Route>
    </Routes>
  );
}

export default App;
